/** Host OS — Marketing. Automation recipes + a broadcast composer (preview). */
import { useState } from "react";
import { toast } from "sonner";
import {
  Megaphone, Zap, Clock, UserX, Repeat, Gift, Send, Mail, Sparkles,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import HostLayout from "@/components/host/HostLayout";
import { HostPageHeader, PreviewBadge } from "@/components/host/hostUi";

const RECIPES = [
  { id: "reminder", icon: Clock, accent: "bg-blue-500/15 text-blue-600 dark:text-blue-300", title: "Event reminder", desc: "Email attendees 24 hours before the event kicks off.", defaultOn: true },
  { id: "winback", icon: Repeat, accent: "bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-300", title: "Win back past attendees", desc: "Nudge players who haven't booked in 30 days to come back.", defaultOn: false },
  { id: "noshow", icon: UserX, accent: "bg-red-500/15 text-red-600 dark:text-red-300", title: "No-show follow-up", desc: "Check in with people who missed an event they booked.", defaultOn: false },
  { id: "welcome", icon: Gift, accent: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300", title: "Welcome new players", desc: "Send a friendly intro the first time someone books with you.", defaultOn: true },
  { id: "fillseats", icon: Zap, accent: "bg-amber-500/15 text-amber-600 dark:text-amber-300", title: "Fill the last seats", desc: "Blast your waitlist when an event is 80% full.", defaultOn: false },
  { id: "review", icon: Sparkles, accent: "bg-primary/15 text-primary-ink", title: "Ask for a review", desc: "Request feedback the day after an event wraps.", defaultOn: false },
];

const HostMarketingPage = () => {
  const [toggles, setToggles] = useState(() => Object.fromEntries(RECIPES.map((r) => [r.id, r.defaultOn])));
  const [audience, setAudience] = useState("all");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const activeCount = Object.values(toggles).filter(Boolean).length;

  const send = () => {
    if (!subject.trim() || !body.trim()) { toast.error("Add a subject and a message first"); return; }
    toast.success("Campaign queued (preview) — sending will go live once wired to Resend");
    setSubject(""); setBody("");
  };

  return (
    <HostLayout>
      <div data-testid="host-marketing-page">
        <HostPageHeader
          icon={Megaphone}
          title="Marketing"
          preview
          subtitle="Put growth on autopilot with automation recipes, then send one-off campaigns to your players."
        />

        {/* Automations */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl">Automations</h2>
          <span className="text-xs text-muted-foreground">{activeCount} of {RECIPES.length} active</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mb-8" data-testid="marketing-recipes">
          {RECIPES.map((r) => {
            const Icon = r.icon;
            const on = toggles[r.id];
            return (
              <Card key={r.id} className={`p-4 transition-colors ${on ? "border-primary/40" : "border-border"}`} data-testid={`recipe-${r.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${r.accent}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-sm">{r.title}</p>
                      <Switch
                        checked={on}
                        onCheckedChange={(v) => { setToggles((p) => ({ ...p, [r.id]: v })); toast.message(v ? `${r.title} turned on` : `${r.title} turned off`); }}
                        data-testid={`recipe-toggle-${r.id}`}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{r.desc}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Campaign composer */}
        <div className="flex items-center gap-2 mb-3">
          <Mail className="w-5 h-5 text-primary-ink" />
          <h2 className="font-display text-xl">New campaign</h2>
          <PreviewBadge />
        </div>
        <Card className="p-5" data-testid="marketing-composer">
          <div className="space-y-4">
            <div>
              <Label className="text-sm">Audience</Label>
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger className="mt-1.5" data-testid="campaign-audience"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All attendees</SelectItem>
                  <SelectItem value="upcoming">Upcoming event attendees</SelectItem>
                  <SelectItem value="past">Past attendees</SelectItem>
                  <SelectItem value="members">Season-pass members</SelectItem>
                  <SelectItem value="lapsed">Lapsed players (30d+)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. New Sunday sessions just dropped 🎾" className="mt-1.5" data-testid="campaign-subject" />
            </div>
            <div>
              <Label className="text-sm">Message</Label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write something your players will love…" rows={6} className="mt-1.5" data-testid="campaign-body" />
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-muted-foreground">Sends from your verified Pouls sender. Delivery goes live once connected to Resend.</p>
              <Button onClick={send} className="gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="campaign-send">
                <Send className="w-4 h-4" /> Queue campaign
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </HostLayout>
  );
};

export default HostMarketingPage;
